import i18next from "i18next";
import dayjs from "dayjs";
// Registering a dayjs bundle is a side effect, and `dayjs.locale(name)` silently does NOTHING when
// `name` was never registered — it leaves the previous global locale in place. The imports and the
// DAYJS_LOCALES table below must therefore live in the same module: `dayjsLocale` can only ever
// name a bundle this file has loaded. Adding a locale means adding both, together.
import "dayjs/locale/ar-sa";
import "dayjs/locale/ur";
import "dayjs/locale/hi";
import "dayjs/locale/bn-bd";
import "dayjs/locale/fr";
import "dayjs/locale/zh-cn";

/** Every locale the app ships — keep in step with I18n.available_locales in config/application.rb. */
export const LOCALES = ["en", "ar", "ur", "hi", "bn", "fr", "zh-Hans"] as const;

export type AppLocale = (typeof LOCALES)[number];

/** LOCALES indexed by lower-cased tag. BCP 47 tags are case-insensitive; object keys are not. */
const CANONICAL_TAGS = new Map<string, string>(LOCALES.map((tag) => [tag.toLowerCase(), tag]));

/**
 * Narrows whatever tag i18next reports to one of LOCALES, by longest prefix.
 *
 * This used to be `tag.split("-")[0]` — keep the first subtag — which is right for the regional
 * tags i18next actually emits ("ar-SA", "en-GB") and silently wrong for "zh-Hans", the first
 * shipped tag whose second subtag is a *script* and carries the whole meaning. It collapsed to
 * "zh", which is in none of the tables below, so a Chinese reader got the English label, the
 * English font stack (no CJK glyphs at all — the page renders as tofu) and the English calendar,
 * with nothing thrown (POS-1939).
 *
 * So: try the whole tag, then drop one subtag at a time. "zh-Hans-CN" finds "zh-Hans", "ar-SA"
 * still finds "ar", and "zh-Hans" is never truncated. An unknown tag lands on "en", which is
 * where every table below already sent it.
 */
export const currentLocale = (language?: string): string => {
  const subtags = (language ?? i18next.language ?? "en").trim().split("-").filter(Boolean);

  for (let length = subtags.length; length > 0; length -= 1) {
    const match = CANONICAL_TAGS.get(subtags.slice(0, length).join("-").toLowerCase());
    if (match) return match;
  }

  return "en";
};

// Direction is a property of the language, not of the script family: Urdu is written in the
// Perso-Arabic script and is right-to-left, while Hindi and Bengali are left-to-right. Deriving
// direction from "is this Arabic?" lays Urdu out backwards; deriving it from "is this a new
// language?" mirrors Hindi and Bengali by mistake.
const RTL_LOCALES = new Set<string>(["ar", "ur"]);

/** True for Arabic and Urdu. Ask this for anything about layout: direction, placement, arrows. */
export const isRtl = (language?: string): boolean => RTL_LOCALES.has(currentLocale(language));

/**
 * True for Arabic only, and deliberately separate from `isRtl`.
 *
 * Choosing `ar_name` over `en_name`, or an Arabic terms-and-conditions page over the English one,
 * is a question about the language of the *content* — which exists in Arabic and English only —
 * and says nothing about how the page is laid out. An Urdu reader is RTL but has no Urdu master
 * data, so they get the English text in a right-to-left page.
 */
export const readsArabicContent = (language?: string): boolean => currentLocale(language) === "ar";

// The name of each language, written in that language — the label a reader recognises as their own.
// This is neither a content question nor a direction one: an Urdu reader is shown "اردو", not the
// Arabic label readsArabicContent() would give them and not the English one their master data gets.
// One table, so the header's language menu and the footer never name a locale differently.
const LOCALE_LABELS: Record<string, string> = {
  en: "English",
  ar: "العربية",
  ur: "اردو",
  hi: "हिन्दी",
  bn: "বাংলা",
  fr: "Français",
  "zh-Hans": "简体中文",
};

export const localeLabel = (language?: string): string =>
  LOCALE_LABELS[currentLocale(language)] ?? LOCALE_LABELS.en;

// LamaSans is the Qoyod brand face, shared with the Android POS and Web POS, and covers Latin and
// Arabic script — so it serves en, ar and ur. Cairo stays as the second step for the Arabic-script
// locales because it is the face this app shipped before.
//
// Neither carries Devanagari or Bengali glyphs, so hi and bn add the matching Noto face (loaded in
// the layouts alongside Cairo). CSS resolves a stack per glyph, not per string: Latin text and
// digits still render in LamaSans while the Indic characters fall through to Noto.
//
// French is the one added language that needs NO new face: it is Latin script and LamaSans already
// carries the accented capitals (É, À, Ç). Simplified Chinese is the opposite — nothing bundled
// anywhere in the product carries a single Han glyph, so without Noto Sans SC every Chinese screen
// is tofu. The web app can pull it from Google Fonts (see the layouts); the Android POS cannot,
// which is why the tag lives here and the font question is a per-client one.
const FONT_FAMILIES: Record<string, string> = {
  en: "LamaSans, Cairo, sans-serif",
  ar: "LamaSans, Cairo, sans-serif",
  ur: "LamaSans, Cairo, sans-serif",
  hi: "LamaSans, 'Noto Sans Devanagari', sans-serif",
  bn: "LamaSans, 'Noto Sans Bengali', sans-serif",
  fr: "LamaSans, Cairo, sans-serif",
  "zh-Hans": "LamaSans, 'Noto Sans SC', sans-serif",
};

export const localeFontFamily = (language?: string): string =>
  FONT_FAMILIES[currentLocale(language)] ?? FONT_FAMILIES.en;

// "en" is dayjs's built-in locale and needs no import; the other six are imported at the top of
// this file. The names are dayjs bundle ids, not app locales — "ar-sa" and "bn-bd" are the
// regional bundles that pair with the antd ar_EG and bn_BD packs, and dayjs spells Simplified
// Chinese "zh-cn" because it has no script-subtag bundle at all.
const DAYJS_LOCALES: Record<string, string> = {
  en: "en",
  ar: "ar-sa",
  ur: "ur",
  hi: "hi",
  bn: "bn-bd",
  fr: "fr",
  "zh-Hans": "zh-cn",
};

/**
 * The dayjs bundle to render calendars and dates with — always one this module has registered.
 *
 * Pass the result to `dayjs.locale(...)`. Handing dayjs a name it has no bundle for does nothing
 * at all and silently leaves the bundle that was applied last, so the date picker's month and
 * weekday names drift away from the rest of the page: `dayjs.locale(i18next.language)` gave a
 * Hindi user whichever calendar happened to be applied before them.
 */
export const dayjsLocale = (language?: string): string =>
  DAYJS_LOCALES[currentLocale(language)] ?? DAYJS_LOCALES.en;

/** Puts dayjs on this app's locale. Returns the bundle name that was applied. */
export const applyDayjsLocale = (language?: string): string => {
  const bundle = dayjsLocale(language);
  dayjs.locale(bundle);
  return bundle;
};
